import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../layout/Spinner";
import Slider from "react-slick";
import Playlist from "./Playlist";
import WhatsOn from "../whatson/WhatsOn";
import News from "../banners/News";
import Message from "../banners/Message";
import Weather from "../banners/Weather";
import Tide from "../banners/Tide";
import WeatherTideCombined from "../banners/WeatherTideCombined";
import PropTypes from "prop-types";
import styles from "./Screen.module.scss";

const Screen = ({ screen, getScreen }) => {
  const { id } = useParams();

  const slider = useRef();
  const nodeUpdatedRef = useRef(screen.data?.changed);

  const playCarousel = () => slider.current?.slickPlay();
  const pauseCarousel = () => slider.current?.slickPause();
  const nextSlide = (currentSlide) => slider.current?.slickGoTo(currentSlide + 1);

  // Wake Lock
  useEffect(() => {
    if ("wakeLock" in navigator) {
      let wakeLock = null;
      const requestWakeLock = async () => {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          console.log("Screen wake lock activated");
        } catch (err) {
          console.error("Error requesting screen wake lock:", err);
        }
      };
      requestWakeLock();
      return () => {
        wakeLock?.release();
        console.log("Screen wake lock released");
      };
    }
  }, []);

  // Fetch screen on mount
  useEffect(() => {
    getScreen(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for changes every 60s safely
  useEffect(() => {
    const intervalID = setInterval(() => {
      try {
        getScreen(id);

        // Screen is coming from props
        if (!screen?.data) return;

        if (screen.data.changed !== nodeUpdatedRef.current) {
          nodeUpdatedRef.current = screen.data.changed;
          console.log("Screen content updated from API");
        }
      } catch (err) {
        console.error("Error fetching screen data:", err);
      }
    }, 60000);

    return () => clearInterval(intervalID);
  }, [getScreen, id, screen]);

  if (!screen.data) {
    return <Spinner />;
  }

  const layout = screen.data.field_screen_layout;

  if (layout === "external_data_full") {
    return <WhatsOn data={screen.data} fullscreen />;
  }

  // Slick settings
  const settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    fade: true,
    cssEase: "ease",
    autoplay: true,
    pauseOnHover: false,
    draggable: false,
    speed: 0,
    beforeChange: (next) => (settings.activeSlide = next),
    afterChange: (activeSlide) => {
      const currentSlideEl = document.getElementById(`slide-${activeSlide}`);
      if (!currentSlideEl) return;

      const videoEl = currentSlideEl.querySelector("video");
      if (videoEl) {
        // Pause carousel while video plays
        pauseCarousel();

        // Stop video if still playing and restart cleanly (no reload flicker)
        videoEl.pause();
        if (videoEl.currentTime > 0.1) {
          videoEl.currentTime = 0;
        }

        const playVideo = async () => {
          try {
            await videoEl.play();
          } catch (err) {
            console.warn("Autoplay blocked:", err);
          }
        };

        // Only trigger load if video isn’t buffered yet
        if (videoEl.readyState < 3) {
          const onCanPlay = () => {
            videoEl.removeEventListener("canplay", onCanPlay);
            playVideo();
          };
          videoEl.addEventListener("canplay", onCanPlay);
          videoEl.load();
        } else {
          playVideo();
        }

        const handleEnded = () => {
          cleanup();
          playCarousel();

          // Move carousel first
          nextSlide(activeSlide);

          // Wait until carousel has visually switched
          setTimeout(() => {
            // Hide video during reset to prevent flash
            videoEl.style.visibility = "hidden";
            videoEl.currentTime = 0;

            // Show it again
            requestAnimationFrame(() => {
              videoEl.style.visibility = "visible";
            });
          }, 500);
        };

        // Freeze detection
        let lastTime = videoEl.currentTime;
        const freezeTimer = setInterval(() => {
          if (!videoEl.paused && !videoEl.ended) {
            const diff = Math.abs(videoEl.currentTime - lastTime);
            if (diff < 0.05) {
              console.warn("Video appears frozen, skipping to next slide...");
              cleanup();
              playCarousel();
              nextSlide(activeSlide);
              videoEl.currentTime = 0;
            } else {
              lastTime = videoEl.currentTime;
            }
          }
        }, 8000); // gentler, less CPU use on Firestick

        const cleanup = () => {
          videoEl.removeEventListener("ended", handleEnded);
          clearInterval(freezeTimer);
        };

        videoEl.addEventListener("ended", handleEnded);

        // Preload next slide’s video for smoother transition
        const nextVideo = document.querySelector(`#slide-${activeSlide + 1} video`);
        if (nextVideo && nextVideo.readyState < 3) {
          nextVideo.load();
        }
      } else {
        // No video on this slide — keep carousel moving
        playCarousel();
      }
    },
  };

  return (
    <div className={`${styles.screen} ${styles[layout]}`}>
      <div className={styles.main}>
        <Slider
          ref={slider}
          {...settings}
          autoplaySpeed={Number(screen.data.field_playlist_reference.field_slide_duration) * 1000}
        >
          {screen.data.field_playlist_reference.field_playlist
            .filter((published) => published.status)
            .map((include, index) => (
              <div key={include.id} id={`slide-${index}`}>
                <Playlist playlist={include} index={index} />
              </div>
            ))}
        </Slider>
      </div>

      {layout === "external_data_sidebar" && <WhatsOn data={screen.data} />}

      {layout === "weatherbar_bottom" && (
        <Weather
          coordinates={screen.data.field_screen_location}
          city={screen.data.field_screen_address.locality}
          limit={6}
        />
      )}

      {layout === "tidetimesbar_bottom" && <Tide location={screen.data.field_tide_location} />}

      {layout === "weathertidetimesbar_bottom" && (
        <WeatherTideCombined
          coordinates={screen.data.field_screen_location}
          city={screen.data.field_screen_address.locality}
          location={screen.data.field_tide_location}
        />
      )}

      {layout === "newsbar_bottom" && <News />}

      {layout === "messagebar_bottom" && <Message message={screen.data.field_screen_message} />}
    </div>
  );
};

Screen.propTypes = {
  screen: PropTypes.object.isRequired,
  getScreen: PropTypes.func.isRequired,
};

export default Screen;
